import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  CircularProgress, 
  Alert, 
  Grid, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip
} from '@mui/material';
import { 
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Warning as WarningIcon,
  CalendarToday as CalendarIcon,
  LocationOn as LocationIcon,
  Category as CategoryIcon,
  Description as DescriptionIcon,
  Inventory as InventoryIcon,
  Notes as NotesIcon,
  LocalOffer as BrandIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Import prediction components
import ItemPrediction from '../components/prediction/ItemPrediction';
import ConsumptionRecorder from '../components/prediction/ConsumptionRecorder';
import ConsumptionHistory from '../components/prediction/ConsumptionHistory';

// Create an Axios instance with default configuration
const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '', // Use environment variable or empty string
});

// Add a request interceptor to include the auth token in all requests
API.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Styled components
const DetailsPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 1.5,
  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
}));

const DetailRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  marginBottom: theme.spacing(1.5),
  alignItems: 'flex-start',
  '&:last-child': {
    marginBottom: 0,
  },
}));

const DetailLabel = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  color: theme.palette.text.secondary,
  width: '140px',
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  '& .MuiSvgIcon-root': {
    fontSize: '1rem',
    marginRight: theme.spacing(1),
  },
}));

const DetailValue = styled(Typography)(({ theme }) => ({
  flex: 1,
}));

const LowStockChip = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.palette.error.light,
  color: theme.palette.error.dark,
  fontWeight: 600,
  fontSize: '0.75rem',
  marginLeft: theme.spacing(1),
}));

const ExpiredChip = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.palette.error.light,
  color: theme.palette.error.dark,
  fontWeight: 600,
  fontSize: '0.75rem',
  marginLeft: theme.spacing(1),
}));

function ItemDetailsPage() {
  const { itemId } = useParams();
  const navigate = useNavigate();
  
  const [item, setItem] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    async function fetchItemDetails() {
      try {
        setIsLoading(true);
        setError('');
        
        console.log(`Fetching details for item ${itemId}...`);
        const response = await API.get(`/api/inventory/${itemId}`);
        console.log('Item details received:', response.data);
        
        setItem(response.data);
      } catch (error) {
        console.error('Error fetching item details:', error);
        setError('Failed to load item details. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    }
    
    if (itemId) {
      fetchItemDetails();
    }
  }, [itemId, refreshTrigger]);

  const handleDelete = async () => {
    try {
      await API.delete(`/api/inventory/${itemId}`);
      navigate('/inventory');
    } catch (error) {
      console.error('Error deleting item:', error);
      setError('Failed to delete item. Please try again.');
    }
  };

  const handleConsumptionRecorded = () => {
    // Refresh the item details
    setRefreshTrigger(prev => prev + 1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };

  const isExpired = (dateString) => {
    if (!dateString) return false;
    
    const expiryDate = new Date(dateString);
    const today = new Date();
    
    return expiryDate < today;
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading item details...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}>
        <Alert 
          severity="error" 
          sx={{ mb: 3 }}
          action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={() => navigate('/inventory')}
            >
              Back to Inventory
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  if (!item) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4, textAlign: 'center' }}>
        <Typography variant="h5" gutterBottom>
          Item Not Found
        </Typography>
        <Typography variant="body1" paragraph>
          The requested item could not be found.
        </Typography>
        <Button 
          variant="contained" 
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/inventory')}
        >
          Back to Inventory
        </Button>
      </Box>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.3 }}
    >
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 4,
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 2, sm: 0 }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton 
              component={RouterLink} 
              to="/inventory" 
              sx={{ mr: 1 }}
              color="primary"
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" component="h1" fontWeight="bold">
              {item.item_name}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<EditIcon />}
              component={RouterLink}
              to={`/inventory/edit/${itemId}`}
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setShowDeleteConfirm(true)}
            >
              Delete
            </Button>
          </Box>
        </Box>

        <Grid container spacing={3}>
          {/* Left column - Item details */}
          <Grid item xs={12} md={6}>
            <DetailsPaper>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                <InventoryIcon sx={{ mr: 1, color: 'primary.main' }} />
                Basic Information
              </Typography>
              
              <DetailRow>
                <DetailLabel>
                  <CategoryIcon />
                  Category:
                </DetailLabel>
                <DetailValue>{item.category}</DetailValue>
              </DetailRow>
              
              {item.description && (
                <DetailRow>
                  <DetailLabel>
                    <DescriptionIcon />
                    Description:
                  </DetailLabel>
                  <DetailValue>{item.description}</DetailValue>
                </DetailRow>
              )}
              
              <DetailRow>
                <DetailLabel>
                  <InventoryIcon />
                  Quantity:
                </DetailLabel>
                <DetailValue>
                  {item.quantity} {item.unit_of_measure}
                  {item.low_stock_threshold && item.quantity <= item.low_stock_threshold && (
                    <LowStockChip 
                      label="Low Stock" 
                      size="small" 
                      icon={<WarningIcon fontSize="small" />}
                    />
                  )}
                </DetailValue>
              </DetailRow>
              
              {item.low_stock_threshold !== null && (
                <DetailRow>
                  <DetailLabel>
                    <WarningIcon />
                    Low Stock Alert:
                  </DetailLabel>
                  <DetailValue>
                    Below {item.low_stock_threshold} {item.unit_of_measure}
                  </DetailValue>
                </DetailRow>
              )}
              
              {item.location && (
                <DetailRow>
                  <DetailLabel>
                    <LocationIcon />
                    Location:
                  </DetailLabel>
                  <DetailValue>{item.location}</DetailValue>
                </DetailRow>
              )}
              
              {item.purchase_date && (
                <DetailRow>
                  <DetailLabel>
                    <CalendarIcon />
                    Purchased:
                  </DetailLabel>
                  <DetailValue>{formatDate(item.purchase_date)}</DetailValue>
                </DetailRow>
              )}
              
              {item.expiry_date && (
                <DetailRow>
                  <DetailLabel>
                    <CalendarIcon />
                    Expires:
                  </DetailLabel>
                  <DetailValue>
                    {formatDate(item.expiry_date)}
                    {isExpired(item.expiry_date) && (
                      <ExpiredChip 
                        label="Expired" 
                        size="small" 
                        icon={<WarningIcon fontSize="small" />}
                      />
                    )}
                  </DetailValue>
                </DetailRow>
              )}
              
              {item.brand && (
                <DetailRow>
                  <DetailLabel>
                    <BrandIcon />
                    Brand:
                  </DetailLabel>
                  <DetailValue>{item.brand}</DetailValue>
                </DetailRow>
              )}
              
              {(item.item_weight !== null && item.item_weight_unit) && (
                <DetailRow>
                  <DetailLabel>
                    <WarningIcon />
                    Weight:
                  </DetailLabel>
                  <DetailValue>
                    {item.item_weight} {item.item_weight_unit}
                  </DetailValue>
                </DetailRow>
              )}
              
              {item.notes && (
                <DetailRow>
                  <DetailLabel>
                    <NotesIcon />
                    Notes:
                  </DetailLabel>
                  <DetailValue>{item.notes}</DetailValue>
                </DetailRow>
              )}
            </DetailsPaper>
            
            {/* Consumption History */}
            <ConsumptionHistory itemId={itemId} />
          </Grid>
          
          {/* Right column - Predictions and consumption recording */}
          <Grid item xs={12} md={6}>
            {/* Item Prediction */}
            <ItemPrediction itemId={itemId} onConsumptionRecorded={handleConsumptionRecorded} />
            
            {/* Consumption Recorder */}
            <ConsumptionRecorder item={item} onConsumptionRecorded={handleConsumptionRecorded} />
          </Grid>
        </Grid>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">
          Delete Item
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{item.item_name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteConfirm(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleDelete} 
            color="error" 
            variant="contained"
            startIcon={<DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </motion.div>
  );
}

export default ItemDetailsPage;